import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { productosApi } from '../../utils/api';

export default function ImportarProductos() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [productos, setProductos] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentName: '' });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));

    if (!validExtensions.includes(fileExt)) {
      toast.error('Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV');
      return;
    }

    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const parseFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (jsonData.length < 2) {
          toast.error('El archivo no contiene datos');
          return;
        }

        // Get headers from first row
        const headers = jsonData[0].map(h => String(h || '').toLowerCase().trim());

        // Map column indices
        const colMap = {
          codigo: findColumnIndex(headers, ['product', 'producto', 'codigo', 'code', 'sku']),
          descripcion: findColumnIndex(headers, ['description', 'descripcion', 'desc']),
          codigoCabys: findColumnIndex(headers, ['código cabys', 'codigo cabys', 'cabys', 'codigo_cabys']),
          categoria: findColumnIndex(headers, ['category', 'categoria']),
          subcategoria: findColumnIndex(headers, ['subcategory', 'subcategoria']),
          unidadMedida: findColumnIndex(headers, ['udm', 'unidad', 'unit', 'udm ventas']),
          esServicio: findColumnIndex(headers, ['inventario', 'no es ítem', 'no es item', 'servicio', 'is service', 'es servicio']),
          isActive: findColumnIndex(headers, ['está activo', 'esta activo', 'activo', 'active', 'is active']),
          precio: findColumnIndex(headers, ['precio', 'price', 'valor']),
          tarifaIva: findColumnIndex(headers, ['iva', 'tarifa', 'tax', 'impuesto'])
        };

        // Parse data rows
        const parsedProducts = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          const codigo = colMap.codigo >= 0 ? String(row[colMap.codigo] || '').trim() : '';
          const descripcion = colMap.descripcion >= 0 ? String(row[colMap.descripcion] || '').trim() : '';

          // Skip empty rows
          if (!codigo && !descripcion) continue;

          const producto = {
            codigo: codigo || `PROD-${i}`,
            nombre: codigo,  // El código como nombre corto
            descripcion: descripcion,  // La descripción completa del producto
            codigoCabys: colMap.codigoCabys >= 0 ? String(row[colMap.codigoCabys] || '').trim() || null : null,
            categoria: colMap.categoria >= 0 ? String(row[colMap.categoria] || '').trim() : null,
            subcategoria: colMap.subcategoria >= 0 ? String(row[colMap.subcategoria] || '').trim() : null,
            unidadMedida: mapUnidadMedida(colMap.unidadMedida >= 0 ? row[colMap.unidadMedida] : 'Unid'),
            esServicio: colMap.esServicio >= 0 ? parseBoolean(row[colMap.esServicio]) : false,
            isActive: colMap.isActive >= 0 ? parseBoolean(row[colMap.isActive]) : true,
            precio: colMap.precio >= 0 ? parseFloat(row[colMap.precio]) || 0 : 0,
            tarifaIva: colMap.tarifaIva >= 0 ? parseFloat(row[colMap.tarifaIva]) || 13 : 13,
            moneda: 'USD'
          };

          parsedProducts.push(producto);
        }

        setProductos(parsedProducts);
        setPreviewMode(true);
        setResults(null);
        toast.success(`${parsedProducts.length} productos encontrados`);
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Error al leer el archivo');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const findColumnIndex = (headers, possibleNames) => {
    for (const name of possibleNames) {
      const idx = headers.findIndex(h => h.includes(name));
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const parseBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const v = value.toLowerCase().trim();
      return v === 'yes' || v === 'si' || v === 'sí' || v === 'true' || v === '1';
    }
    return false;
  };

  const mapUnidadMedida = (value) => {
    if (!value) return 'Unid';
    const v = String(value).toLowerCase().trim();

    const mapping = {
      'unidad': 'Unid',
      'unit': 'Unid',
      'each': 'Unid',
      'kilogramo': 'Kg',
      'kg': 'Kg',
      'gramo': 'g',
      'g': 'g',
      'litro': 'L',
      'l': 'L',
      'mililitro': 'mL',
      'ml': 'mL',
      'metro': 'm',
      'm': 'm',
      'hora': 'h',
      'h': 'h',
      'día': 'd',
      'dia': 'd',
      'day': 'd',
      'd': 'd',
      'servicio': 'Sp',
      'sp': 'Sp'
    };

    return mapping[v] || 'Unid';
  };

  const handleImport = async () => {
    if (productos.length === 0) {
      toast.error('No hay productos para importar');
      return;
    }

    setImporting(true);
    setPreviewMode(false);
    setProgress({ current: 0, total: productos.length, currentName: '' });

    const batchSize = 10;
    const totalResults = { imported: 0, updated: 0, errors: [] };

    try {
      for (let i = 0; i < productos.length; i += batchSize) {
        const batch = productos.slice(i, i + batchSize);

        setProgress({
          current: Math.min(i + batchSize, productos.length),
          total: productos.length,
          currentName: batch[0]?.codigo || batch[0]?.descripcion || ''
        });

        try {
          const response = await productosApi.import(batch);
          const result = response.data?.data || response.data;
          totalResults.imported += result.imported || 0;
          totalResults.updated += result.updated || 0;
          if (result.errors?.length > 0) totalResults.errors.push(...result.errors);
        } catch (err) {
          totalResults.errors.push({ codigo: batch[0]?.codigo, error: err.message });
        }
      }

      setProgress({ current: productos.length, total: productos.length, currentName: 'Completado' });
      setResults(totalResults);
      toast.success('Importación completada');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error al importar productos');
      setResults(totalResults);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Producto', 'Descripcion', 'Codigo CABYS', 'Categoria', 'Subcategoria', 'UDM', 'Servicio', 'Activo', 'Precio', 'IVA'],
      ['SRV-001', 'Servicio de consultoría TI', '8531100000000', 'Servicios', 'Consultoría', 'Sp', 'Si', 'Si', 150, 13],
      ['PROD-001', 'Cable UTP Cat6 305m', '4529000000000', 'Materiales', 'Cableado', 'Unid', 'No', 'Si', 85.50, 13],
    ]);
    ws['!cols'] = [{ wch: 12 }, { wch: 35 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 6 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 6 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos');
    XLSX.writeFile(wb, 'plantilla_productos.xlsx');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      parseFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/productos')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Importar Productos</h1>
            <p className="text-sm text-gray-500">Importe productos desde un archivo Excel o CSV</p>
          </div>
        </div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-2 px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 font-medium text-sm"
        >
          <Download className="h-4 w-4" />
          Descargar Plantilla
        </button>
      </div>

      {/* Upload Zone */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
        >
          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Arrastre un archivo aquí
          </h3>
          <p className="text-gray-500 mb-4">o haga clic para seleccionar</p>
          <p className="text-sm text-gray-400">
            Formatos soportados: .xlsx, .xls, .csv
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* File Selected */}
      {file && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* File Info */}
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-8 w-8 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">
                  {productos.length} productos encontrados
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFile(null);
                setProductos([]);
                setResults(null);
              }}
              className="text-gray-500 hover:text-red-600 text-sm"
            >
              Cambiar archivo
            </button>
          </div>

          {/* Progress */}
          {importing && (
            <div className="p-6 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Importando productos...</h3>
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-gray-600">Progreso: {progress.current} de {progress.total}</span>
                <span className="text-gray-900 font-medium">
                  {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2 truncate">
                Procesando: <span className="font-medium">{progress.currentName}</span>
              </p>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="p-6 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Resultados de la importación</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{results.imported || 0}</p>
                  <p className="text-sm text-green-600">Nuevos</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{results.updated || 0}</p>
                  <p className="text-sm text-blue-600">Actualizados</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <XCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{results.errors?.length || 0}</p>
                  <p className="text-sm text-red-600">Errores</p>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-2">Errores:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {results.errors.map((err, i) => (
                      <li key={i}>
                        {typeof err === 'string'
                          ? err
                          : `${err.codigo || 'Error'}: ${err.error || JSON.stringify(err)}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => navigate('/productos')}
                  className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Ver Productos
                </button>
              </div>
            </div>
          )}

          {/* Preview Table */}
          {previewMode && productos.length > 0 && (
            <>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">#</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Código</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Nombre</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">CABYS</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Categoría</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Unidad</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Servicio</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Activo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {productos.slice(0, 50).map((p, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-2 font-mono text-xs">{p.codigo}</td>
                        <td className="px-4 py-2">
                          <div className="font-medium">{p.nombre}</div>
                          {p.descripcion && p.descripcion !== p.nombre && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">{p.descripcion}</div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {p.codigoCabys ? (
                            <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-mono">
                              {p.codigoCabys}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {p.categoria && (
                            <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs">
                              {p.categoria}
                            </span>
                          )}
                          {p.subcategoria && (
                            <span className="inline-block px-2 py-0.5 bg-gray-50 rounded text-xs ml-1">
                              {p.subcategoria}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{p.unidadMedida}</td>
                        <td className="px-4 py-2 text-center">
                          {p.esServicio ? (
                            <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {p.isActive ? (
                            <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {productos.length > 50 && (
                  <div className="p-3 bg-gray-50 text-center text-sm text-gray-500">
                    Mostrando 50 de {productos.length} productos
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Se importarán <strong>{productos.length}</strong> productos
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setFile(null);
                      setProductos([]);
                    }}
                    className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="px-6 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2 transition-colors"
                  >
                    {importing ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Importar Productos
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Formato del archivo</h4>
        <p className="text-sm text-blue-700 mb-2">
          El archivo debe contener las siguientes columnas (los nombres pueden variar):
        </p>
        <ul className="text-sm text-blue-600 list-disc list-inside space-y-1">
          <li><strong>Product / Código:</strong> Código único del producto</li>
          <li><strong>Description / Descripción:</strong> Descripción del producto</li>
          <li><strong>Código Cabys / CABYS:</strong> Código CABYS para Hacienda (opcional)</li>
          <li><strong>Category / Categoría:</strong> Categoría (opcional)</li>
          <li><strong>Subcategory / Subcategoría:</strong> Subcategoría (opcional)</li>
          <li><strong>UdM Ventas / Unidad:</strong> Unidad de medida (opcional, default: Unid)</li>
          <li><strong>No es Ítem de Inventario:</strong> Si es servicio (opcional)</li>
          <li><strong>Está Activo:</strong> Si está activo (opcional, default: Sí)</li>
        </ul>
      </div>
    </div>
  );
}
